import {
  MIN_PASSWORD_LENGTH,
  mobilePhoneSchema,
  passwordSchema,
  personNameSchema,
} from '@hm/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Field, TextInput } from '~/components/ui/field'
import { FormError } from '~/components/ui/primitives'
import { api, messageOf } from '~/lib/api'
import { useT } from '~/lib/i18n'
import { AuthLayout } from './auth-layout'

const setupSchema = z.object({
  platformName: z.string().trim().min(2, 'Enter a name.').max(120),
  name: personNameSchema,
  phone: mobilePhoneSchema,
  password: passwordSchema,
})
type SetupInput = z.infer<typeof setupSchema>

/**
 * First-run only. The API refuses once any account exists, so this cannot be used
 * to mint a second owner (core/services/bootstrap.ts).
 */
export function SetupPage() {
  const t = useT()
  const queryClient = useQueryClient()
  const form = useForm<SetupInput>({
    resolver: zodResolver(setupSchema),
    defaultValues: { platformName: 'Hardware Mediator', name: '', phone: '', password: '' },
  })

  const setup = useMutation({
    mutationFn: async (input: SetupInput) => {
      await api.post('/api/bootstrap', input)
      // Sign straight in, so setup ends in the app rather than at a login form.
      await api.post('/api/auth/login', { phone: input.phone, password: input.password })
    },
    onSuccess: () => queryClient.invalidateQueries(),
  })

  return (
    <AuthLayout
      title={t('setup.set_up_your_platform')}
      subtitle={t('setup.this_creates_the_owner_account_it')}
    >
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => setup.mutate(values))}
        noValidate
      >
        <Field
          label={t('common.business_name')}
          hint={t('setup.shown_to_your_customers_and_suppliers')}
          error={form.formState.errors.platformName?.message}
          required
        >
          {(id) => (
            <TextInput
              id={id}
              invalid={Boolean(form.formState.errors.platformName)}
              {...form.register('platformName')}
            />
          )}
        </Field>

        <Field label={t('setup.your_name')} error={form.formState.errors.name?.message} required>
          {(id) => (
            <TextInput
              id={id}
              autoComplete="name"
              invalid={Boolean(form.formState.errors.name)}
              {...form.register('name')}
            />
          )}
        </Field>

        <Field
          label={t('setup.your_mobile_number')}
          hint={t('setup.you_will_sign_in_with_this')}
          error={form.formState.errors.phone?.message}
          required
        >
          {(id) => (
            <TextInput
              id={id}
              type="tel"
              inputMode="numeric"
              accept="phone"
              prefix="+977"
              placeholder="98-1234-5678"
              invalid={Boolean(form.formState.errors.phone)}
              {...form.register('phone')}
            />
          )}
        </Field>

        <Field
          label={t('common.password')}
          hint={t('setup.at_least_minpasswordlength_characters', {
            minpasswordlength: MIN_PASSWORD_LENGTH,
          })}
          error={form.formState.errors.password?.message}
          required
        >
          {(id) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="new-password"
              invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
          )}
        </Field>

        {setup.error ? <FormError>{messageOf(setup.error)}</FormError> : null}

        <Button type="submit" size="lg" full loading={setup.isPending}>
          {t('setup.create_owner_account')}
        </Button>
      </form>
    </AuthLayout>
  )
}
